import humps from "humps";
import moment from "moment";
import { filterNull } from "cxa-utils";
import * as Consts from "actions/claimActionTypes";

export const claimCategories = [
  {
    id: "ghsClaims",
    label: "Insurance GHS Claims",
  },
  {
    id: "insuranceClaims",
    label: "Insurance Outpatient Claims",
  },
  {
    id: "flexClaims",
    label: "Flex Claims",
  },
];

const initialState = {
  configuredClaimItemFields: {
    fields: [],
    error: null,
  },
  accounts: {
    byId: {},
    all: [],
    pageFields: [],
    isFetching: false,
    isFetched: false,
    error: null,
    didInvalidate: false,
    lastUpdated: null,
  },
  fields: {
    byId: {},
    isFetching: false,
    didInvalidate: false,
    lastUpdated: null,
    claimInstruction: null,
    consentMessage: null,
    consentMessageTitle: null,
  },
  claimItemGroups: {
    byId: {},
    all: [],
  },
  claimItems: {
    all: [],
    byId: {},
    isFetching: false,
    didInvalidate: false,
    lastUpdated: null,
  },
  claimItemAccounts: {
    all: [],
    byId: {},
    isFetching: false,
    didInvalidate: false,
    lastUpdated: null,
  },
  claimPlanYears: {
    all: [],
    byId: {},
    isFetching: false,
    didInvalidate: false,
    lastUpdated: null,
  },
  benefitPlans: {
    all: [],
    byId: {},
    isFetching: false,
    didInvalidate: false,
    lastUpdated: null,
  },
  claimList: {
    byId: {},
    byType: {},
    isFetching: false,
    didInvalidate: false,
    lastUpdated: null,
    pendingPageFields: [],
    hisPageFields: [],
  },
  deleteClaim: {
    status: false,
  },
  claimLimitMessages: {
    byId: {},
    isFetching: false,
  },
  foreignReceiptAmount: null,
  receiptAmount: null,
  receiptCurrency: "",
  toCurrencyCode: "",
  downloadPendingClaimsUrl: null,
  downloadHistoricalClaimsUrl: null,
  employeesByDelegation: {
    all: [],
    byId: {},
    isFetching: false,
  },
  disclaimer: "",
  balanceDetails: [],
};

const transformClaimItemAccount = ({ accountDetail, ...account }) => ({
  ...account,
  ...Object.keys(accountDetail).reduce((obj, key) => {
    const value = accountDetail[key];
    return {
      ...obj,
      [key]: value.hasOwnProperty("value") ? value.value : value,
    };
  }, {}),
});

const transformFieldType = field => {
  if (field.dataType === "List" && (!field.items || field.items.length == 0)) {
    return "String";
  } else if (field.enableOthers && !field.disabled) {
    return "Autocomplete";
  }
  return field.dataType;
};

const normalizeClaimFields = fields => {
  return fields.map(
    ({ fieldId, name, value, parentFieldId, childFieldId, validDateRange, dataType, ...rest }) => {
      const additionalProps = {
        ...(validDateRange
          ? {
              minDate: moment(validDateRange.start),
              maxDate: moment(validDateRange.end),
            }
          : {}),
        ...(value && Array.isArray(value)
          ? {
              items: value.map(item => ({ value: item.id, label: item.value })),
            }
          : { value: value }),
        ...(parentFieldId ? { parentName: humps.camelize(parentFieldId) } : {}),
        ...(childFieldId ? { childName: humps.camelize(childFieldId) } : {}),
      };

      const transformedReceiptCurrency = {
        ...(fieldId === "ReceiptCurrency" && value
          ? {
              dataType: "Currency",
              // filter unique items
              items: [...new Set(value.map(item => item.CurrencyFrom))].map(item => ({
                value: item,
                label: item,
              })),
              // currency converter configuration
              config: humps.camelizeKeys(value),
            }
          : {}),
      };

      return {
        name: humps.camelize(fieldId),
        fieldLowercased: fieldId.toLowerCase(),
        label: name,
        dataType,
        ...additionalProps,
        ...transformedReceiptCurrency,
        ...rest,
      };
    }
  );
};

const transformClaimFieldEntries = (fields, page) =>
  fields.map(item => {
    if (
      item.parentName &&
      (page === "add" || page === "edit" || item.parentName !== "receiptCurrency")
    ) {
      return null;
    }

    const children = fields.filter(field => item.name === field.parentName);
    const additionalProps = {
      ...(item.childName && item.childName.includes("SpecialOthers") ? { enableOthers: true } : {}),
      ...(children.length > 0 ? { children } : {}),
    };

    const field = { ...item, ...additionalProps };

    return {
      ...field,
      dataType: transformFieldType(field),
    };
  });

const fieldSortCompareFn = (a, b) => a.displayOrder - b.displayOrder;

const transformClaimFields = ({ fields, uploadReceipt, uploadReferral }, attachments, page) => {
  const receiptIncluded =
    uploadReceipt.isRequired && uploadReceipt.isRequired.toLowerCase() !== "none";
  const hasSupportingDocuments = Array.isArray(attachments) && attachments.some(
    item => item.field === "Receipt" 
  );
  const referralIncluded =
    uploadReferral.isRequired && uploadReferral.isRequired.toLowerCase() !== "none";

  const transformedUploadReceipt = receiptIncluded || hasSupportingDocuments
    ? {
        ...uploadReceipt,
        dataType: "Receipt",
        name: "receipts",
        label: uploadReceipt.name || "Receipt Upload",
        included: receiptIncluded,
        required: receiptIncluded && uploadReceipt.isRequired.toLowerCase() !== "optional",
      }
    : null;

  const transformedUploadReferral = referralIncluded
    ? {
        ...uploadReferral,
        dataType: "Referral",
        label: uploadReferral.fieldName,
        name: "referral",
        included: referralIncluded,
        required:
          referralIncluded != "None" && uploadReferral.isRequired.toLowerCase() != "optional",
      }
    : null;
  const normalizedFields = normalizeClaimFields(fields);

  const [
    actualFields,
    documentId,
    claimItemType,
    taxStatus,
    cPFContributable,
  ] = normalizedFields.reduce(
    (results, item) => {
      if (item.name === "documentID") {
        results[1] = item;
      } else if (item.name === "claimItemType") {
        results[2] = item;
      } else if (item.name === "taxStatus") {
        results[3] = item;
      } else if (item.name === "cPFContributable") {
        results[4] = item;
      } else {
        results[0] = [].concat(results[0], item);
      }
      return results;
    },
    [[], null, null, null]
  );

  const transformedFields = transformClaimFieldEntries(actualFields, page);

  const unsortedFields = []
    .concat(transformedFields, transformedUploadReceipt, transformedUploadReferral)
    .filter(filterNull);

  const sortedFields = unsortedFields.sort(fieldSortCompareFn);

  return {
    fields: sortedFields,
    documentId,
    claimItemType,
    taxStatus,
    cPFContributable,
    uploadReceipt: transformedUploadReceipt,
    uploadReferral: transformedUploadReferral,
  };
};

export const normalizeClaim = ({ attachments, ...claimData }) => {
  const defaultProps = { receipts: [], referral: null };

  const { receipts, referral } =
    attachments && attachments.length > 0
      ? attachments.reduce(
          (obj, item) =>
            item.field == "Receipt"
              ? { ...obj, receipts: [...obj.receipts, item] }
              : { ...obj, referral: item },
          defaultProps
        )
      : defaultProps;

  const lowercasedKeys = Object.entries(claimData).reduce((a, [key, value]) => {
    a[key.toLowerCase()] = value;
    return { ...a, receipts, referral };
  }, {});

  return { ...claimData, receipts, referral, lowercasedKeys };
};

const parseClaimListById = (state, claimListPayload) =>
  claimCategories.reduce(
    (obj, category) =>
      claimListPayload[category.id].reduce(
        (currObj, item) => ({
          ...currObj,
          [item.objectId]: normalizeClaim(item),
        }),
        obj
      ),
    state.byId
  );

const parseClaimListByType = (state, claimListPayload) =>
  claimCategories.reduce(
    (obj, category) => ({
      ...obj,
      [category.id]: claimListPayload[category.id].map(item => item.objectId),
    }),
    {}
  );

export default (state = initialState, action = {}) => {
  //eslint-disable-line complexity

  switch (action.type) {
    case Consts.FETCH_ACCOUNTS_BEGIN: {
      return {
        ...state,
        accounts: {
          ...state.accounts,
          isFetching: true,
          isFetched: false,
          error: null,
        },
      };
    }

    case Consts.FETCH_ACCOUNTS_SUCCESS: {
      return {
        ...state,
        accounts: {
          ...state.accounts,
          byId: {
            ...state.accounts.byId,
            [action.meta.planYear]: action.payload.accountList.accountDtos,
          },
          pageFields: action.payload.accountList.pageFields,
          uiConfigs: action.payload.uiConfigs,
          isFetching: false,
          isFetched: true,
          error: null,
        },
      };
    }

    case Consts.FETCH_ACCOUNTS_ERROR: {
      return {
        ...state,
        accounts: {
          ...state.accounts,
          byId: {},
          pageFields: [],
          isFetching: false,
          isFetched: false,
          error: action.payload,
        },
      };
    }

    case Consts.FETCH_PENDING_CLAIMS_BEGIN:
    case Consts.FETCH_CLAIMS_BEGIN:
    case Consts.FETCH_CLAIM_BEGIN:
      return {
        ...state,
        claimList: {
          ...state.claimList,
          byId: {
            ...state.claimList.byId,
            [action.claimId]: {},
          },
          isFetching: true,
        },
        deleteClaim: {
          status: false,
        },
      };

    case Consts.FETCH_CLAIMS_SUCCESS:
      return {
        ...state,
        claimList: {
          ...state.claimList,
          isFetching: false,
          byId: parseClaimListById(state.claimList, action.payload),
          byType: {
            ...state.claimList.byType,
            [action.meta.claimListType]: parseClaimListByType(state.claimList, action.payload),
          },
          hisPageFields: action.payload.pageFields,
        },
        downloadHistoricalClaimsUrl: action.payload.downloadHistoricalClaimsUrl,
        disclaimer: action.payload.disclaimerTxt,
      };

    case Consts.FETCH_CLAIM_SUCCESS:
      return {
        ...state,
        claimList: {
          ...state.claimList,
          isFetching: false,
          byId: {
            ...state.claimList.byId,
            [action.claimId]: normalizeClaim(action.payload),
          },
        },
      };

    case Consts.FETCH_PENDING_CLAIMS_SUCCESS:
      return {
        ...state,
        claimList: {
          ...state.claimList,
          isFetching: false,
          byId: parseClaimListById(state.claimList, action.payload),
          byType: {
            ...state.claimList.byType,
            pending: parseClaimListByType(state.claimList, action.payload),
          },
          pendingPageFields: action.payload.pageFields,
        },
        deleteClaim: {
          status: false,
        },
        downloadPendingClaimsUrl: action.payload.downloadPendingClaimsUrl,
        disclaimer: action.payload.disclaimerTxt,
      };

    case Consts.FETCH_CLAIM_ITEMS_BEGIN:
      return {
        ...state,
        claimItems: {
          ...state.claimItems,
          isFetching: true,
        },
        claimPlanYears: {
          ...state.claimPlanYears,
          isFetching: true,
        },
      };

    case Consts.DISPLAY_CLAIM_ITEM_FIELDS_REQUEST: {
      return {
        ...state,
        configuredClaimItemFields: {
          fields: [],
          error: null,
        },
      };
    }

    case Consts.DISPLAY_CLAIM_ITEM_FIELDS_SUCCESS: {
      return {
        ...state,
        configuredClaimItemFields: {
          fields: action.payload,
          error: null,
        },
      };
    }

    case Consts.DISPLAY_CLAIM_ITEM_FIELDS_FAILURE: {
      return {
        ...state,
        configuredClaimItemFields: {
          fields: [],
          error: action.payload,
        },
      };
    }

    case Consts.FETCH_CLAIM_LIMIT_MESSAGES_REQUEST:
      return {
        ...state,
        claimLimitMessages: {
          ...state.claimLimitMessages,
          isFetching: true,
        },
      };

    case Consts.FETCH_CLAIM_LIMIT_MESSAGES_SUCCESS: {
      const { claimantId, claimantType, claimItemId, receiptDate } = action.meta;
      const key = "".concat(claimantId + claimantType + claimItemId + receiptDate);

      const messages = action.payload.map(x => ({
        label: x.message.replace(/:$/, ""),
        value: x.value,
      }));

      return {
        ...state,
        claimLimitMessages: {
          byId: {
            ...state.claimLimitMessages.byId,
            [key]: messages,
          },
          isFetching: false,
        },
      };
    }

    case Consts.FETCH_CLAIM_LIMIT_MESSAGES_FAILURE:
      return {
        ...state,
        claimLimitMessages: {
          ...state.claimLimitMessages,
          isFetching: false,
        },
      };

    case Consts.FETCH_CLAIM_ITEMS_SUCCESS: {
      const [
        groupsById,
        groupsAll,
        categoriesById,
        categoriesAll,
        claimsById,
        claimsAll,
      ] = action.payload.groupedClaimItems.reduce(
        (res, group) => {
          const [
            prevGroupsById,
            prevGroupsAll,
            prevCategoriesById,
            prevCategoriesAll,
            prevClaimsById,
            prevClaimsAll,
          ] = res;

          const { groupName, claimItems } = group;
          const camelizedGroupName = humps.camelize(groupName);

          const [
            groupCategoriesById,
            groupCategoriesAll,
            groupClaimsById,
            groupClaimsAll,
          ] = claimItems.reduce(
            (catRes, item) => {
              const [
                prevGroupCatById,
                prevGroupCatAll,
                prevGroupClaimsById,
                prevGroupClaimsAll,
              ] = catRes;
              const { categoryName, claimId, categoryImage } = item;
              const camelizedCategoryName = humps.camelize(categoryName);
              const category = prevGroupCatById[camelizedCategoryName];
              const groupCatById = {
                ...prevGroupCatById,
                [camelizedCategoryName]: {
                  id: camelizedCategoryName,
                  label: categoryName,
                  image: categoryImage,
                  items: !category ? [claimId] : [...category.items, claimId],
                },
              };
              const groupCatAll = !category
                ? [...prevGroupCatAll, camelizedCategoryName]
                : prevGroupCatAll;

              return [
                groupCatById,
                groupCatAll,
                { ...prevGroupClaimsById, [item.claimId]: item },
                [...prevGroupClaimsAll, item.claimId],
              ];
            },
            [{}, [], {}, []]
          );

          return [
            {
              ...prevGroupsById,
              [camelizedGroupName]: {
                id: camelizedGroupName,
                label: groupName,
                categories: groupCategoriesAll,
              },
            },
            [...prevGroupsAll, camelizedGroupName],
            { ...prevCategoriesById, ...groupCategoriesById },
            [...prevCategoriesAll, ...groupCategoriesAll],
            { ...prevClaimsById, ...groupClaimsById },
            { ...prevClaimsAll, ...groupClaimsAll },
          ];
        },
        [{}, [], {}, [], {}, []]
      );

      return {
        ...state,
        claimItemGroups: {
          byId: groupsById,
          all: groupsAll,
        },
        claimItemCategories: {
          byId: categoriesById,
          all: categoriesAll,
        },
        claimItems: {
          isFetching: false,
          byId: claimsById,
          all: claimsAll,
          claimNotes: action.payload.claimNotes,
          isCPFEnabled: action.payload.isCPFEnabled,
        },
        claimPlanYears: {
          isFetching: false,
          byId: action.payload.planYears.reduce((obj, item) => ({ ...obj, [item.id]: item }), {}),
          all: action.payload.planYears.map(item => item.id),
        },
      };
    }

    case Consts.FETCH_CLAIM_ITEM_FIELDS_BEGIN:
      return {
        ...state,
        fields: {
          ...state.fields,
          isFetching: true,
        },
      };

    case Consts.FETCH_CLAIM_ITEM_FIELDS_SUCCESS: {
      const { claimItemId, planId, page } = action.meta;
      const payload = Array.isArray(action.payload.attachments) ? action.payload.fields : action.payload;
      return {
        ...state,
        fields: {
          ...state.fields,
          isFetching: false,
          byId: {
            ...state.fields.byId,
            [planId]: {
              ...(state.fields.byId[planId] || {}),
              [claimItemId]: transformClaimFields(payload, action.payload.attachments, page),
            },
          },
          claimInstruction: payload.claimInstruction,
          consentMessage: payload.consentMessage,
          consentMessageTitle: payload.consentMessageTitle,
        },
      };
    }

    case Consts.FETCH_BENEFIT_PLANS_SUCCESS:
      return {
        ...state,
        benefitPlans: {
          ...state.benefitPlans,
          isFetching: false,
          byId: action.payload.reduce((obj, item) => ({ ...obj, [item.planID]: item }), {}),
          all: action.payload.map(item => item.planID),
        },
      };
    case Consts.FETCH_BALANCE_DETAIL_SUCCESS:
      return {
        ...state,
        balanceDetails: [...action.payload],
      };

    case Consts.FETCH_BALANCE_DETAIL_ERROR:
      return {
        ...state,
        balanceDetails: [],
      };
    case Consts.FETCH_CLAIM_ITEM_ACCOUNTS_BEGIN:
      return {
        ...state,
        claimItemAccounts: {
          ...state.claimItemAccounts,
          isFetching: true,
        },
      };

    case Consts.FETCH_CLAIM_ITEM_ACCOUNTS_SUCCESS:
      return {
        ...state,
        claimItemAccounts: {
          ...state.claimItemAccounts,
          isFetching: false,
          byId: {
            ...state.claimItemAccounts.byId,
            [action.meta.claimTypeId]: {
              accountsExhausted: action.payload.accountsExhausted,
              byId: action.payload.accounts.reduce(
                (obj, item) => ({
                  ...obj,
                  [item.id]: transformClaimItemAccount(item),
                }),
                {}
              ),
              all: action.payload.accounts.map(item => item.id),
            },
          },
        },
      };

    case Consts.DELETE_CLAIM_BEGIN:
      return {
        ...state,
        claimList: {
          ...state.claimList,
          isFetching: true,
        },
      };

    case Consts.DELETE_CLAIM_SUCCESS:
      return {
        ...state,
        deleteClaim: { status: action.payload.toUpperCase() === "OK" },
      };

    case Consts.DELETE_CLAIM_ERROR:
      return {
        ...state,
      };

    case Consts.FETCH_EMPLOYEES_BY_DELEGATION_BEGIN:
      return {
        ...state,
        employeesByDelegation: {
          ...state.employeesByDelegation,
          isFetching: true,
        },
      };

    case Consts.FETCH_EMPLOYEES_BY_DELEGATION_SUCCESS:
      return {
        ...state,
        employeesByDelegation: {
          ...state.employeesByDelegation,
          isFetching: false,
          all: action.payload.map(item => item.employeeKey),
          byId: action.payload.reduce((obj, item) => {
            return {
              ...obj,
              [item.employeeKey]: item,
            };
          }, {}),
        },
      };

    case Consts.CHANGE_CURRENCY_CODE:
      return {
        ...state,
        ...action.payload,
      };

    case Consts.CHANGE_CURRENCY_RATE:
      return {
        ...state,
        ...action.payload,
      };

    default: {
      return { ...state, deleteClaim: { status: false } };
    }
  }
};

export const getClaim = (state, id) => state.byId[id];

export const getClaimListByType = (state, type) =>
  state.byType[type]
    ? Object.keys(state.byType[type]).reduce(
        (obj, item) => ({
          ...obj,
          [item]: state.byType[type][item].map(id => getClaim(state, id)),
        }),
        {}
      )
    : {};

export const getAccountsById = (state, id) => state.byId[id];

export const getAccountByType = (state, type, memberId, periodEnd) =>
  state.find(
    item => item.accountType === type && item.memberId === memberId && item.periodEnd === periodEnd
  );

export const getFieldsByPlanIdAndItemType = (state, planId, claimTypeId) =>
  (state.byId[planId] || {})[claimTypeId];

export const getClaimItem = (state, id) => state.byId[id];

export const getAllClaimItems = state => state.all.map(id => getClaimItem(state, id));

export const groupItemsByCategory = (groups, categories, items) =>
  groups.all
    .map(group => {
      const groupDetails = groups.byId[group];
      const groupCategories = groupDetails.categories
        .map(category => {
          const cat = categories.byId[category];
          const catItems = cat.items.map(item => items.byId[item]).filter(item => item.isVisible);

          return catItems.length > 0
            ? {
                ...cat,
                items: catItems,
              }
            : null;
        })
        .filter(filterNull);

      return groupCategories.length > 0
        ? {
            ...groupDetails,
            categories: groupCategories,
          }
        : null;
    })
    .filter(filterNull);

const getFieldAdditionalProps = field => {
  const fieldType = field.dataType;
  switch (fieldType) {
    case "List":
      return { placeholder: `Select ${field.label}` };

    default:
      return {};
  }
};

export const augmentClaimFieldsProps = fields =>
  fields
    .map(field => {
      const fieldType = field.dataType;

      if ((fieldType === "Receipt" || fieldType === "Referral") && !field.included) {
        return null;
      }

      const additionalProps = getFieldAdditionalProps(field);

      return {
        ...field,
        ...additionalProps,
        readOnly: field.disabled,
      };
    })
    .filter(filterNull);

export const getDefaultBenefitPlan = state => state.all.find(id => state.byId[id].isDefault);

export const getBenefitPlan = (state, id) => state.byId[id];

export const getAllBenefitPlans = state => state.all.map(id => getBenefitPlan(state, id));

export const getEmployeeByDelegation = (state, id) => state.byId[id];

export const getAllEmployeeByDelegation = state =>
  state.all.map(id => getEmployeeByDelegation(state, id));

export const getDefaultDelegateEmployeeId = state => (state.all.length === 1 ? state.all[0] : null);

export const getValidExchangeRateValue = ({
  config = [],
  currencyCode,
  receiptCurrency,
  receiptDate,
}) => {
  const validExchangeRates = config.filter(code =>
    currencyCode ? code.currencyFrom === currencyCode : code.currencyFrom === receiptCurrency
  );
  // start and end date are inclusive for isBetween check
  const validExchangeRate = validExchangeRates.find(rates =>
    moment(receiptDate).isBetween(
      moment(rates.effectiveStart).format("YYYY-MM-DD[T]HH:mm:ss"),
      moment(rates.effectiveEnd).format("YYYY-MM-DD[T]HH:mm:ss"),
      null,
      []
    )
  );

  return validExchangeRate ? validExchangeRate : {};
};



// WEBPACK FOOTER //
// ./reducers/claimsReducer.js